import { useState, useCallback, useRef } from 'react';
import explainerService, { type ExplainResponse } from '../services/explainer.service';

export function useExplainSegment(segmentId: number) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [explanation, setExplanation] = useState<ExplainResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ref tracks current explanation to avoid stale closure in useCallback
  const explanationRef = useRef<ExplainResponse | null>(null);

  const explain = useCallback(
    async (transcript: string) => {
      const startTime = Date.now();
      setIsOpen(true);
      setIsLoading(true);
      setError(null);

      try {
        let result: ExplainResponse;

        if (explanationRef.current !== null) {
          // Result already loaded and panel is open: regenerate via POST (D-05 specifics)
          result = await explainerService.postExplanation({ segmentId, transcript });
        } else {
          // GET-first: try DB cache, fall back to Claude (D-05)
          const saved = await explainerService.getExplanation(segmentId);
          if (saved !== null) {
            result = saved;
          } else {
            result = await explainerService.postExplanation({ segmentId, transcript });
          }
        }

        // Enforce minimum 300ms skeleton duration (D-02 agent's discretion)
        const elapsed = Date.now() - startTime;
        const remaining = 300 - elapsed;
        if (remaining > 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, remaining));
        }

        explanationRef.current = result;
        setExplanation(result);
      } catch (err: unknown) {
        const axiosError = err as { response?: { status?: number } };
        if (axiosError?.response?.status === 429) {
          setError("You've reached your AI explanation limit. Please try again in a few minutes.");
        } else {
          setError('Unable to generate explanation. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [segmentId]
  );

  const dismiss = useCallback(() => {
    setIsOpen(false);
    // Note: explanation state is intentionally preserved so re-open shows last result
  }, []);

  // Reset when segment changes (user navigates to different segment)
  const resetForSegment = useCallback(() => {
    setIsOpen(false);
    setIsLoading(false);
    setExplanation(null);
    setError(null);
    explanationRef.current = null;
  }, []);

  return { isOpen, isLoading, explanation, error, explain, dismiss, resetForSegment };
}
